import { apiClient } from "@/services/api-client";
import { shouldUseMockData } from "@/lib/mock-data/utils";

/**
 * Coach ledger (packet-coach-2026-08-21 piece E, consumer half of BAR #11/#12).
 *
 * `GET /trading-groups/{id}/ledger` — one row per ACTIVE account of an
 * aggressive solo-rotation group: who carries today's group goal and why every
 * other account sits out. Every field mirrors the backend payload verbatim
 * (packet piece D contract + L15 `verdict_context` sub-key, amendment #1).
 *
 * The tab is VISIBILITY, not a control (NS "set-and-forget"): nothing here
 * mutates; numbers that the backend could not read arrive as `null` with a
 * reason and render as a warning — never as a green number.
 */

export type LedgerSource = "selection" | "snapshot" | "pre_signal";

/** Filled by the snapshot/API layer from the `CandidateVerdict`; null on pre-signal rows. */
export interface LedgerVerdictContext {
  phase: string | null;
  /** "eval" | "funded" — the account's pass phase, NOT the member dial. */
  posture: string | null;
  eligible: boolean | null;
  reason: string | null;
  rank: number | null;
}

export interface LedgerRow {
  account_id: number;
  firm_id: string | null;
  account_type: string | null;
  variant: string | null;
  variant_source: string | null;
  account_size: number | null;
  equity: number | null;
  remaining_dd_usd: number | null;
  remaining_dd_model: string | null;
  dll_headroom_usd: number | null;
  dll_source: string | null;
  dll_unbounded: boolean;
  realized_today: number | null;
  points_to_target: number | null;
  risk_per_contract_usd: number | null;
  risk_source: string | null;
  attempts: number | null;
  attempts_source: string | null;
  max_affordable_qty: number | null;
  max_contracts: number | null;
  consistency_ceiling_usd: number | null;
  consistency_basis: string | null;
  day_target_usd: number | null;
  day_cap_binding: string | null;
  hours_state: string | null;
  effective_window: string | null;
  posture_dial: string | null;
  unreadable_reason: string | null;
  verdict_context: LedgerVerdictContext | null;
}

export interface GroupLedger {
  group_id: number;
  trading_day: string | null;
  group_daily_usd: number | null;
  day_target_effective: number | null;
  day_cap_binding: string | null;
  consistency_basis: string | null;
  posture: string | null;
  carrier_account_id: number | null;
  selected_account_id: number | null;
  selected_reason: string | null;
  selected_at: string | null;
  source: LedgerSource;
  stale: boolean;
  goal_window_start: string | null;
  rows: LedgerRow[];
}

export const groupLedgerQueryKey = (groupId: number) =>
  ["group-ledger", groupId] as const;

// Poll band: the `copy_status` snapshot (WS) carries the ledger on every solo
// fill / carrier selection / day resolution (L17), so a fresh socket receipt
// relaxes the poll; silence snaps it back so the tab never rests on a bare
// slowdown (same gate shape as copy-status.service.ts).
const LEDGER_POLL_MS = 5_000;
const LEDGER_FAILURE_POLL_MS = 60_000;
const LEDGER_SOCKET_COVERAGE_MS = 12_000;
const LEDGER_GATED_POLL_MS = 15_000;

const lastLedgerAtByGroup = new Map<number, number>();

/** Test seam + logout hygiene. */
export function resetLedgerSnapshotReceipts(): void {
  lastLedgerAtByGroup.clear();
}

function isSocketCovered(groupId: number, nowMs: number): boolean {
  const at = lastLedgerAtByGroup.get(groupId);
  return typeof at === "number" && nowMs - at < LEDGER_SOCKET_COVERAGE_MS;
}

export function ledgerRefetchInterval(
  fetchFailureCount: number,
  groupId: number,
  nowMs: number = Date.now(),
): number {
  const jitter = (groupId % 5) * 500;
  if (fetchFailureCount > 0) return LEDGER_FAILURE_POLL_MS + jitter;
  if (isSocketCovered(groupId, nowMs)) return LEDGER_GATED_POLL_MS + jitter;
  return LEDGER_POLL_MS + jitter;
}

/** A ledger payload is usable only when it names its group and carries rows. */
export function isGroupLedger(value: unknown): value is GroupLedger {
  const l = value as Partial<GroupLedger> | null | undefined;
  return (
    !!l &&
    typeof l === "object" &&
    !Array.isArray(l) &&
    typeof l.group_id === "number" &&
    Array.isArray(l.rows)
  );
}

/**
 * L17: the `copy_status` socket snapshot gains a `ledger` key = the GET
 * payload. Write it straight into `["group-ledger", id]` (BAR #12: ≤5 s after
 * a fill). A snapshot without a ledger (mirror group, older backend) is a
 * no-op — it must never clear or downgrade the cached ledger.
 */
export function applyLedgerSnapshot(
  queryClient: { setQueryData: (key: unknown[], data: unknown) => unknown },
  payload: unknown,
  atMs: number = Date.now(),
): boolean {
  const snapshot = payload as { ledger?: unknown } | null | undefined;
  const ledger = snapshot && typeof snapshot === "object" ? snapshot.ledger : undefined;
  if (!isGroupLedger(ledger)) return false;
  queryClient.setQueryData([...groupLedgerQueryKey(ledger.group_id)], ledger);
  lastLedgerAtByGroup.set(ledger.group_id, atMs);
  return true;
}

export async function fetchGroupLedger(groupId: number): Promise<GroupLedger> {
  // DEV FIXTURE — localhost demo mode only (`shouldUseMockData` is hard-false
  // off localhost). Lets the tab be walked before piece D's endpoint serves;
  // never reachable in the staging/prod graph.
  if (shouldUseMockData()) {
    const fixture = await import("../__fixtures__/group-ledger.fixture");
    return fixture.devGroupLedger(groupId);
  }
  const response = await apiClient.get<GroupLedger>(
    `/trading-groups/${groupId}/ledger`,
  );
  if (!isGroupLedger(response.data)) {
    throw new Error("Ledger payload is not a group ledger");
  }
  return response.data;
}
